import { afterRenderEffect, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { NgxOtpInputComponent, NgxOtpInputComponentOptions, NgxOtpStatus } from "ngx-otp-input";
import { SupabaseService } from "../../../../../core/supabase.service";
import { AudienceService } from "../../../../../core/tracking/audience.service";
import { GtmService } from "../../../../../core/tracking/gtm.service";
import { NotificationsService } from "../../../../../services/notifications.service";
import { enableOtpAutofill } from "../../../otp-autofill";
import { PASSWORD_PATTERN } from "../../../password-rules";
import { ALREADY_REGISTERED_MESSAGE, signUpErrorMessage } from "./sign-up-errors";
import { BtnComponent } from "../../../../../components/ui/buttons/btn/btn.component";
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  iconoirArrowRight,
  iconoirMail,
  iconoirLock,
  iconoirCheckCircle,
  iconoirRefreshDouble,
  iconoirCreditCardSlash,
  iconoirWarningCircle
} from '@ng-icons/iconoir';

@Component({
  selector: 'app-sign-up-flow',
  imports: [
    ReactiveFormsModule,
    BtnComponent,
    NgIconComponent,
    NgxOtpInputComponent,
    RouterLink
  ],
  providers: [
    provideIcons({
      iconoirArrowRight,
      iconoirMail,
      iconoirLock,
          iconoirCheckCircle,
      iconoirRefreshDouble,
      iconoirCreditCardSlash,
      iconoirWarningCircle
    })
  ],
  templateUrl: './sign-up-flow.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sign-up-flow.component.scss'
})
export class SignUpFlowComponent {

  private readonly supabase = inject(SupabaseService);
  private readonly audience = inject(AudienceService);
  private readonly gtm = inject(GtmService);
  private readonly notifications = inject(NotificationsService);
  private readonly fb = inject(NonNullableFormBuilder);

  /** Règle commune (`password-rules.ts`) : tout caractère spécial accepté (MOKA-360). */
  public readonly passwordPattern = PASSWORD_PATTERN;

  public readonly form = this.fb.group({
    email: new FormControl('', [Validators.required, Validators.email]),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required, Validators.minLength(8), Validators.pattern(this.passwordPattern)]),
    conditions: new FormControl(false, [Validators.requiredTrue]),
    // Consentement marketing (RGPD) : optionnel, jamais pré-coché.
    optinMarketing: new FormControl(false)
  });

  /**
   * Email à valider, fourni par le lien de l'email de confirmation
   * (/sign-in?verify=<email>) : bascule directement sur l'écran de saisie
   * du code sans repasser par le formulaire d'inscription.
   */
  public readonly verifyEmail = input<string | null>(null);

  public readonly pending = signal(false);
  public readonly emailSent = signal(false);
  public readonly resendPending = signal(false);
  public readonly errorMessage = signal<string | null>(null);
  public readonly showPasswordRules = signal(false);
  /** Erreur du code, affichée sous les cases plutôt qu'en notification éphémère. */
  public readonly otpError = signal<string | null>(null);
  /** Secondes avant de pouvoir renvoyer l'e-mail de confirmation. */
  public readonly resendCountdown = signal(0);

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly otpInput = viewChild(NgxOtpInputComponent);
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Arrivée via le lien « saisir le code » de l'email de confirmation :
    // pré-remplit l'email et ouvre directement l'écran de saisie.
    effect(() => {
      const email = this.verifyEmail();
      if (email) {
        this.form.controls.email.setValue(email);
        this.emailSent.set(true);
      }
    });

    // Cases du code rendues : on autorise la saisie automatique du code reçu par e-mail.
    afterRenderEffect(() => {
      if (this.emailSent()) enableOtpAutofill(this.host.nativeElement);
    });

    inject(DestroyRef).onDestroy(() => this.stopCountdown());
  }

  /** Règles du mot de passe, aussi lues par le gabarit pour dire ce qui manque. */
  public passwordRule(rule: 'length' | 'upper' | 'lower' | 'digit' | 'special'): boolean {
    const value = this.form.controls.password.value ?? '';
    switch (rule) {
      case 'length': return value.length >= 8;
      case 'upper': return /[A-Z]/.test(value);
      case 'lower': return /[a-z]/.test(value);
      case 'digit': return /\d/.test(value);
      case 'special': return /[^A-Za-z0-9]/.test(value);
    }
  }

  // Saisie du code de confirmation reçu par email (alternative au lien)
  public readonly otpPending = signal(false);
  public otpStatus: NgxOtpStatus | null = null;
  public readonly otpOptions: NgxOtpInputComponentOptions = {
    otpLength: 6,
    autoFocus: true,
    autoBlur: true
  };

  public async signUp(): Promise<void> {
    this.form.markAllAsTouched();
    if (!this.form.valid) return;
    this.pending.set(true);
    this.errorMessage.set(null);

    const { email, password, firstName, lastName, optinMarketing } = this.form.getRawValue();
    if (!email || !password) return;
    const origin = window.location.origin;

    // Le nom de l'organisation n'est plus demandé ici : il l'est après la confirmation de
    // l'e-mail (/auth/callback), là où son unicité peut être vérifiée et corrigée (MOKA-360).
    const { data, error } = await this.supabase.client.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${origin}/auth/callback`,
        data: {
          first_name: firstName,
          last_name: lastName,
          // Repris par provision-organisation à la création du profil,
          // puis synchronisé vers Brevo (attribut OPT_IN).
          optin_marketing: optinMarketing === true,
          // MOKA-355 : campagne d'arrivée (UTM + domaine d'origine), copiée en base dans
          // signup_attributions à la création du compte pour mesurer les campagnes.
          ...(this.audience.signupSource() ? { signup_source: this.audience.signupSource() } : {})
        }
      }
    });

    this.pending.set(false);

    // Adresse déjà confirmée : Supabase ne renvoie pas d'erreur (anti-énumération) mais un
    // utilisateur sans identité, et n'envoie aucun e-mail. Sans ce test, la personne
    // attendrait un code qui n'arrivera jamais.
    if (!error && data.user && (data.user.identities?.length ?? 0) === 0) {
      this.errorMessage.set(ALREADY_REGISTERED_MESSAGE);
      return;
    }

    if (error) {
      this.errorMessage.set(signUpErrorMessage(error));
      return;
    }

    // Conversion pour GTM (MOKA-383) : la confirmation se fait ensuite sur /auth/callback, page non suivie.
    this.gtm.signUp('email');
    this.otpError.set(null);
    this.emailSent.set(true);
    this.startCountdown();
  }

  /** Retour au formulaire pour corriger l'adresse : les autres champs sont conservés. */
  public editEmail(): void {
    this.emailSent.set(false);
    this.otpError.set(null);
    this.otpStatus = null;
    this.stopCountdown();
  }

  private readonly router = inject(Router);

  /**
   * Vérifie le code à 6 chiffres reçu dans l'email de confirmation
   * d'inscription (type 'signup'), puis route vers le callback commun
   * qui gère la session et la redirection.
   */
  public async verifySignupCode(event: string[]): Promise<void> {
    const token = event.join('');
    if (token.length !== 6 || this.otpPending()) return;

    const email = this.form.getRawValue().email;
    if (!email) return;

    this.otpPending.set(true);
    this.otpStatus = null;
    this.otpError.set(null);

    const { error } = await this.supabase.client.auth.verifyOtp({
      email,
      token,
      type: 'signup'
    });

    this.otpPending.set(false);

    if (error) {
      this.otpStatus = NgxOtpStatus.FAILED;
      // Supabase répond « expired or is invalid » dans les deux cas : un seul message.
      this.otpError.set('Ce code est incorrect ou a expiré. Vérifiez les 6 chiffres du dernier email reçu, ou cliquez sur « Renvoyer l\'email ».');
      // Cases vidées : sinon la première frappe suivante revérifierait les anciens chiffres.
      this.otpInput()?.reset();
      this.host.nativeElement.querySelector<HTMLInputElement>('.ngx-otp-input-form input')?.focus();
      return;
    }

    this.otpStatus = NgxOtpStatus.SUCCESS;
    this.notifications.showSuccess('Adresse email confirmée');
    await this.router.navigate(['/auth/callback']);
  }

  public async resendEmail(): Promise<void> {
    const email = this.form.getRawValue().email;
    if (!email || this.resendCountdown() > 0) return;
    this.resendPending.set(true);

    const { error } = await this.supabase.client.auth.resend({
      type: 'signup',
      email
    });

    this.resendPending.set(false);

    if (error) {
      this.otpError.set(error.status === 429
        ? 'Trop d\'envois rapprochés. Patientez une minute, puis réessayez.'
        : 'L\'email n\'a pas pu être renvoyé. Vérifiez votre connexion internet, puis réessayez.');
    } else {
      this.otpError.set(null);
      this.notifications.showSuccess('Email de confirmation renvoyé');
    }
    this.startCountdown();
  }

  private startCountdown(seconds = 30): void {
    this.stopCountdown();
    this.resendCountdown.set(seconds);
    this.countdownTimer = setInterval(() => {
      this.resendCountdown.update(c => c - 1);
      if (this.resendCountdown() <= 0) this.stopCountdown();
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.countdownTimer = null;
    this.resendCountdown.set(0);
  }
}
